import React from "react";
import { Button, Dropdown, } from "react-bootstrap";
import { Rnd, DraggableData } from "react-rnd";
import { DesignerData, DesignerElement, NavigationHelper } from "../../helpers";
import { DesignerSidebar } from "./DesignerSidebar";
import WebFont from 'webfontloader';
import { v4 } from 'uuid';

interface Props {
  jsonData?: string,
  updateValue?: (questionId: number, newValue: string) => void,
  onUpdate: (value: string) => void,
}

export const DesignerOverlay: React.FC<Props> = props => {

  const defaultWidth = 80;
  const defaultHeight = 14;

  const [vertical, setVertical] = React.useState(false);
  const [data, setData] = React.useState<DesignerData>(null);
  const [selectedIndex, setSelectedIndex] = React.useState(-1);
  const [fontList, setFontList] = React.useState<string[]>(null)
  const [resizedKey, setResizedKey] = React.useState("");
  const [jsonData, setJsonData] = React.useState(props.jsonData);
  const [showGrid, setShowGrid] = React.useState(false);

  const elGrid: DesignerElement = {
    type: 'grid',
    left: 0,
    top: 0,
    width: 100,
    height: 100,
    gap: 4,
    rows: 12,
    columns: 12,
    key: v4().replaceAll("-", ""),
  };

  const vhToPx = (vh: number) => {
    const totalHeight = document.getElementById('designerScreen').clientHeight;
    return totalHeight * vh * .01;
  }


  const loadJson = () => {
    fetch(jsonData)
      .then(response => response.json())
      .then((d: DesignerData) => {
        const keyList: string[] = []
        d.scenes[0].elements.forEach(e => {
          if (!e.key) e.key = v4().replaceAll("-", "");
          else if (keyList.indexOf(e.key)) e.key = v4().replaceAll("-", "");
          else keyList.push(e.key);
        })
        setData(d)
      });
  }

  const init = () => {
    let search = new URLSearchParams(window.location.search);
    let shape = search.get("shape");
    setVertical(shape === "9:16");

    if (!jsonData) setData({ scenes: [] });
    else if (jsonData.startsWith("http")) loadJson();
    else {
      try {
        const d: DesignerData = JSON.parse(jsonData);
        if (d.scenes.length > 0) d.scenes[0].elements.forEach(e => { if (!e.key) e.key = v4().replaceAll("-", ""); })
        setData(d);
      } catch (error) {
        console.error("Invalid JSON data in DesignerOverlay:", error);
        setData({ scenes: [] });
      }
    }
  }

  const convertWidth = (percent: number) => {
    //return percent.toString() + "%"
    return document.getElementById('designerScreen').clientWidth * percent / 100.0;
  }

  const convertHeight = (percent: number) => {
    return document.getElementById('designerScreen').clientHeight * percent / 100.0;
  }

  const handleDragStop = (dd: DraggableData) => {
    const screen = document.getElementById('designerScreen');
    const xPercent = Math.round(dd.x / screen.clientWidth * 10000) / 100.0;
    const yPercent = Math.round(dd.y / screen.clientHeight * 10000) / 100.0;
    const d = { ...data };

    const key = dd.node.dataset.key;
    const el = getElementByKey(d, key);
    if (el) {
      el.left = xPercent;
      el.top = yPercent;
      setData(d);
    }
  }

  const handleResizeStop = (e: any, dir: any, elementRef: any, delta: any, position: any) => {
    const screen = document.getElementById('designerScreen');
    const xPercent = Math.round(position.x / screen.clientWidth * 10000) / 100.0;
    const yPercent = Math.round(position.y / screen.clientHeight * 10000) / 100.0;

    const widthDeltaPercent = Math.round(delta.width / screen.clientWidth * 10000) / 100.0;
    const heightDeltaPercent = Math.round(delta.height / screen.clientHeight * 10000) / 100.0;
    const d = { ...data };
    const key = elementRef.dataset.key;
    const el = getElementByKey(d, key);
    if (el) {
      el.left = xPercent;
      el.top = yPercent;
      el.width = (el.width || defaultWidth) + widthDeltaPercent;
      el.height = (el.height || defaultHeight) + heightDeltaPercent;
      setData(d);
    }
  }

  const getElementByKey = (d: DesignerData, key: string) => {
    let result: DesignerElement = null;
    d.scenes[0].elements?.forEach(el => {
      if (el.key === key) result = el;
    })
    return result;
  }


  const getElements = () => {
    const result: React.ReactElement[] = [];

    const grid: React.ReactElement | null  = !!showGrid? (<Rnd key={elGrid.key} data-key={elGrid.key} onMouseDown={(e) => { setSelectedIndex(44) }} >
      {showGridElement()}
    </Rnd>): null;

    let i = 0;
    if (data?.scenes?.length > 0) {
      for (let el of data.scenes[0].elements) {
        const className = (i === selectedIndex) ? "selected" : "";
        const idx = i;

        const defaultProps = { x: convertWidth(el.left || 0), y: convertHeight(el.top || 0), width: convertWidth((el.width || defaultWidth) + (el.border?.width || 0) * 2), height: convertHeight((el.height || defaultHeight) + (el.border?.width || 0) * 2), }

        result.push(<Rnd key={el.key} data-key={el.key} default={defaultProps} lockAspectRatio={el.lockAspectRatio || false} bounds="parent" className={className} onMouseDown={(e) => { setSelectedIndex(idx) }} onDragStop={(e, d) => { handleDragStop(d) }} onResizeStop={handleResizeStop} >
          {getElementContents(el)}
        </Rnd>);
        i++;
      }
    }
    if (grid) {
      return [
        ...result.slice(0, 1),
        grid,
        ...result.slice(1)
      ];
    }
    return result;
  }

  const getElementContents = (el: DesignerElement) => {
    let result = <>Type not implemented: {el.type}</>
    switch (el.type) {
      case "box":
        const boxStyle: React.CSSProperties = { backgroundColor: el.color, opacity: (el.opacity || 100) * 0.01, height: "100%" }
        if (el.border && el.border.type) boxStyle.border = vhToPx(el.border.width || 1) + "px " + el.border.type + " " + el.border.color || "#000000";
        result = <div style={boxStyle}></div>
        break;
      case "image":
        result = <img src={el.url} alt="Element" className="designerImage" />
        break;
      case "text":
        // lineHeight: vhToPx(el.size || 10) + "px",
        const style: React.CSSProperties = {
          fontSize: vhToPx(el.size || 10) + "px",
          lineHeight: vhToPx((el.size || 10) * (el.lineHeight || 1.5)) + "px",
          textAlign: el.align || "left",
        }
        if (el.bold) style.fontWeight = "bold";
        if (el.italic) style.fontStyle = "italic";
        if (el.underline) style.textDecoration = "underline";
        if (el.textShadow) style.textShadow = "0.05em 0.05em 0.05em #000";
        if (el.color) style.color = el.color;
        if (el.font) style.fontFamily = el.font;

        result = <div className="designerText" style={style} dangerouslySetInnerHTML={{ __html: el.text?.replaceAll("\n", "<br/>") }} ></div>
        break;
    }
    return result;
  }


  const showGridElement = () => {

    const gridStyle: React.CSSProperties = {
      position: "relative",
      width: `${convertWidth(elGrid.width)}px`,
      height: `${convertHeight(elGrid.height)}px`,
    };
    const gridOverlayStyle: React.CSSProperties = {
      gridTemplateColumns: `repeat(${elGrid.columns}}, 1fr)`,
      gridTemplateRows: `repeat(${elGrid.rows}}, 1fr)`,
    };

    return (
        <div style={gridStyle}>
          <div className="grid-overlay" style={gridOverlayStyle}>
            {[...Array((elGrid.rows && elGrid.columns)? elGrid.rows * elGrid.columns: 25)].map((_, index) => (
                <div key={index}></div>
            ))}
          </div>
        </div>
    );
  }

  const handleSidebarUpdateIndex = (requestedIndex: number) => {
    if (requestedIndex < 0) requestedIndex = 0;
    if (requestedIndex > data.scenes[0].elements.length - 1) requestedIndex = data.scenes[0].elements.length - 1;
    if (requestedIndex !== selectedIndex) {
      const d = { ...data };
      const elements = [...d.scenes[0].elements]
      let el = elements.splice(selectedIndex, 1);
      elements.splice(requestedIndex, 0, el[0]);
      d.scenes[0].elements = elements;
      setSelectedIndex(-1);
      setData(null);
      setTimeout(() => {
        setSelectedIndex(requestedIndex);
        setData(d);
      }, 5);

    }
  }

  const handleSidebarUpdate = (newElement: DesignerElement) => {
    const d = { ...data };
    d.scenes[0].elements.splice(selectedIndex, 1, newElement);
    setData(d);
  }

  const handleSidebarRemove = () => {
    const d = { ...data };
    console.log(d);
    d.scenes[0].elements.splice(selectedIndex, 1);
    setData(d);
    setSelectedIndex(-1);
    console.log(d);
  }

  const handleSidebarDuplicate = () => {
    const d = { ...data };
    const copy = { ...data.scenes[0].elements[selectedIndex] }
    copy.top = copy.top - 3;
    if (copy.top < 0) copy.top = 0;
    copy.key = v4().replaceAll("-", "");
    d.scenes[0].elements.push(copy);

    setData(d);
    setSelectedIndex(-1);
  }

  const checkFonts = () => {
    const fonts: string[] = [];
    if (data?.scenes?.length > 0) {
      data?.scenes[0].elements.forEach(el => {
        if (el.font && fonts.indexOf(el.font) === -1) fonts.push(el.font);
      });
    }
    if (fonts !== fontList) setFontList(fonts);
  }

  const loadFonts = () => {
    if (fontList && fontList.length > 0) {
      WebFont.load({
        google: { families: fontList }
      });
    }
  }

  const addElement = (type: string) => {
    let el: DesignerElement = null;
    const key = v4().replaceAll("-", "");
    switch (type) {
      case "text":
        el = { type: 'text', left: 25, top: 25, width: 50, height: 20, text: 'Hello World', font: 'Roboto', size: 14, align: 'left', bold: false, italic: false, underline: false, color: '#FFFF00', key: key };
        break;
      case "box":
        el = { type: 'box', left: 0, top: 67, width: 100, height: 33, color: '#000099', opacity: 100, key: key }
        break;
      case "image":
        el = { type: 'image', left: 25, top: 20, width: 50, height: 50, lockAspectRatio: true, url: 'https://admin.signpresenter.com/images/designer/flag.jpg', key: key }
        break;
    }
    if (el) {
      const d = { ...data };
      d.scenes[0].elements.push(el);
      setData(d);
      setSelectedIndex(d.scenes[0].elements.length - 1);
    }

  }

  React.useEffect(() => { setJsonData(props.jsonData) }, [props.jsonData]); // eslint-disable-line
  React.useEffect(init, [jsonData]); // eslint-disable-line
  React.useEffect(checkFonts, [data]); // eslint-disable-line
  React.useEffect(loadFonts, [fontList]); // eslint-disable-line


  React.useEffect(() => {
    let timeout: any;
    const handleResize = () => {
      clearTimeout(timeout);
      timeout = setTimeout(() => { setResizedKey(NavigationHelper.getRefreshKey()); }, 500);
    }
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []); // eslint-disable-line

  React.useEffect(() => {

    if (resizedKey !== "") {
      const d = { ...data };
      d.scenes[0].delay++;
      if (d.scenes[0].delay === 2) d.scenes[0].delay = 0; //cache buster to make sure json changes.
      const json = JSON.stringify(d);

      setData({ scenes: [] });
      setTimeout(() => {
        setJsonData(json)
      }, 500)
      //setJsonData(json);
    }
  }, [resizedKey]); // eslint-disable-line


  return (
    <div id="designerOverlay">
      <div id="designerHeader">
        <span className="float-right" style={{ paddingTop: 5 }}>
          <Dropdown style={{ marginRight: 15, display: "inline-block" }}>
            <Dropdown.Toggle variant="primary" id="dropdown-basic">Add Element</Dropdown.Toggle>
            <Dropdown.Menu>
              <Dropdown.Item href="about:blank" onClick={(e) => { e.preventDefault(); addElement("text") }}>Text</Dropdown.Item>
              <Dropdown.Item href="about:blank" onClick={(e) => { e.preventDefault(); addElement("image") }}>Image</Dropdown.Item>
              <Dropdown.Item href="about:blank" onClick={(e) => { e.preventDefault(); addElement("box") }}>Box</Dropdown.Item>
            </Dropdown.Menu>
          </Dropdown>
          <Button style={{ marginRight: 15 }} variant="success" onClick={(e) => { e.preventDefault(); props.onUpdate(JSON.stringify(data)) }}>Save Changes</Button>
          <Button variant={showGrid? "danger": "success"} onClick={(e) => { e.preventDefault(); setShowGrid(!showGrid) }}>{showGrid? "Hide Grid": "Show Grid"}</Button>
        </span>

        <img src="/images/logo.png" alt="logo"></img>

      </div>
      <div id="designerContent">
        <DesignerSidebar selectedIndex={selectedIndex} updateIndex={handleSidebarUpdateIndex} selectedElement={(selectedIndex === -1) ? null : data?.scenes[0]?.elements[selectedIndex]} updateValue={handleSidebarUpdate} remove={handleSidebarRemove} duplicate={handleSidebarDuplicate} />
        <div id="designerScreenPadding"></div>
        <div id="designerScreen" className={(vertical) ? "vertical" : ""}>
          {getElements()}
        </div>
      </div>
    </div >
  );

}

